import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../JourneyConfigDetail.css';

const API_URL = 'http://localhost:5000';

function JourneyConfigDetail({ configId, journeyData, token, onBack, onActivate }) {
  const [journey, setJourney] = useState(journeyData || null);
  const [loading, setLoading] = useState(!journeyData);
  const [error, setError] = useState('');

  useEffect(() => {
    // If journey data was passed in, use it directly
    if (journeyData) {
      setJourney(journeyData);
      setLoading(false);
    } else {
      // Otherwise, try to fetch it
      fetchJourneyConfig();
    }
  }, [configId, journeyData, token]);

  const fetchJourneyConfig = async () => {
    try {
      setLoading(true);
      setError('');

      // Create a timeout promise that rejects after 10 seconds
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );

      // Try to fetch all configs and find the one matching the ID
      // (Since the single-journey endpoint may not exist on the backend)
      const requestPromise = axios.get(
        `${API_URL}/api/journey-configs`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Race between the request and the timeout
      const response = await Promise.race([requestPromise, timeoutPromise]);
      const foundJourney = response.data.configs.find(config => config.id === configId);

      if (foundJourney) {
        setJourney(foundJourney);
      } else {
        setError('Journey configuration not found.');
      }
    } catch (err) {
      const errorMsg = err.message === 'Request timeout'
        ? 'Request timed out. Make sure the backend server is running.'
        : err.response?.data?.message || 'Failed to load journey details. Please try again.';
      setError(errorMsg);
      console.error('Journey config fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return '#27ae60';
      case 'paused':
        return '#f39c12';
      case 'completed':
        return '#3498db';
      default:
        return '#95a5a6';
    }
  };

  if (loading) {
    return (
      <div className="config-detail-container">
        <div className="loading">Loading journey details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="config-detail-container">
        <div className="error-banner">{error}</div>
        <button className="back-button" onClick={onBack}>
          ← Back to List
        </button>
      </div>
    );
  }

  if (!journey) {
    return (
      <div className="config-detail-container">
        <div className="error-banner">Journey not found</div>
        <button className="back-button" onClick={onBack}>
          ← Back to List
        </button>
      </div>
    );
  }

  return (
    <div className="config-detail-container">
      <div className="detail-header">
        <button className="back-button" onClick={onBack}>
          ← Back to List
        </button>
        <h1>Journey Configuration Details</h1>
      </div>

      <div className="detail-card">
        {/* Title and Status */}
        <div className="section">
          <div className="title-section">
            <h2>{journey.title}</h2>
            <span className="status-badge" style={{ backgroundColor: getStatusColor(journey.status) }}>
              {journey.status.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Description */}
        {journey.description && (
          <div className="section">
            <h3 className="section-title">Description</h3>
            <p className="description-text">{journey.description}</p>
          </div>
        )}

        {/* Core Information */}
        <div className="section">
          <h3 className="section-title">Core Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Journey ID:</span>
              <span className="info-value">{journey.id}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Status:</span>
              <span className="info-value">{journey.status}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Created:</span>
              <span className="info-value">{formatDate(journey.created_at)}</span>
            </div>
            {journey.updated_at && (
              <div className="info-item">
                <span className="info-label">Last Updated:</span>
                <span className="info-value">{formatDate(journey.updated_at)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Team & Facilitator */}
        <div className="section">
          <h3 className="section-title">Team & Facilitator</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Facilitator ID:</span>
              <span className="info-value">{journey.facilitator_id}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Team Members Count:</span>
              <span className="info-value">
                {journey.team_member_ids ? journey.team_member_ids.length : 0}
              </span>
            </div>
          </div>
          {journey.team_member_ids && journey.team_member_ids.length > 0 && (
            <div className="members-list">
              <span className="info-label">Team Member IDs:</span>
              <div className="member-tags">
                {journey.team_member_ids.map((memberId, index) => (
                  <span key={index} className="member-tag">
                    {memberId}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Journey Configuration */}
        <div className="section">
          <h3 className="section-title">Journey Configuration</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Max Participants:</span>
              <span className="info-value">{journey.max_participants}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Duration:</span>
              <span className="info-value">{journey.duration_days} days</span>
            </div>
          </div>
        </div>

        {/* Raw Data (for reference) */}
        <div className="section">
          <h3 className="section-title">Raw Data</h3>
          <pre className="raw-data">{JSON.stringify(journey, null, 2)}</pre>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-buttons">
        <button className="btn btn-secondary" onClick={onBack}>
          Back to List
        </button>
        <button className="btn btn-primary" onClick={() => onActivate(configId)}>
          Activate Journey
        </button>
      </div>
    </div>
  );
}

export default JourneyConfigDetail;
